import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef } from 'react';

import { chartConfig } from '@config/chart';
import {
  useBulkOptionsPricing,
  useOptionsPricing,
} from '@hooks/useOptionsPricing';
import type { Order } from '@services/orderTypes';
import type { Asset } from '@shared/types';
import { brandRgba } from '@utils/color-utils';

import { formatStrikePrice } from '../../../utils/chartHelpers';
import { formatOptionPrice } from '../../../utils/price-formatting';

import { getVisibleStrikes } from './densityUtils';
import { YScale } from './types';

interface StrikePriceLineProps {
  strikePrice: number;
  yScale: YScale;
  innerWidth: number;
  isDraggingStrike: boolean;
  previewStrikePrice: number | null;
  nearestValidStrike?: number | null;
  spotPrice: number;
  asset: Asset;
  onStrikePriceSelect: (price: number) => void;
  onStrikeDragStart: () => void;
  ordersForStrike: Order[];
  onManageOrders?: (orders: Order[], strikePrice: number) => void;
  isManageOrdersPanelOpen?: boolean;
}

export { AllStrikePrices };

export function StrikePriceLine({
  strikePrice,
  yScale,
  innerWidth,
  isDraggingStrike,
  previewStrikePrice,
  nearestValidStrike = null,
  spotPrice,
  asset,
  onStrikePriceSelect,
  onStrikeDragStart,
  ordersForStrike,
  onManageOrders,
  isManageOrdersPanelOpen = false,
}: StrikePriceLineProps) {
  if (!strikePrice || strikePrice <= 0) {
    return null;
  }

  // During drag, the main strike follows the preview position
  const effectiveStrikePrice =
    isDraggingStrike && previewStrikePrice ? previewStrikePrice : strikePrice;
  const effectiveY = yScale(effectiveStrikePrice);

  return (
    <g style={{ pointerEvents: 'all' }}>
      {/* Main strike line - follows drag position */}
      <line
        x1={0}
        y1={effectiveY}
        x2={innerWidth}
        y2={effectiveY}
        stroke="rgba(255, 255, 255, 0.6)"
        strokeWidth={3}
        strokeDasharray="4,4"
        style={{ pointerEvents: 'none' }}
      />

      {/* Ghost pill at snap target during drag */}
      {isDraggingStrike &&
        nearestValidStrike &&
        nearestValidStrike !== effectiveStrikePrice && (
          <g>
            {/* Ghost line at snap target */}
            <line
              x1={0}
              y1={yScale(nearestValidStrike)}
              x2={innerWidth}
              y2={yScale(nearestValidStrike)}
              stroke="rgba(255, 255, 255, 0.3)"
              strokeWidth={3}
              strokeDasharray="4,4"
              style={{ pointerEvents: 'none' }}
            />
            {/* Ghost pill at snap target */}
            <PillRenderer
              strikePrice={nearestValidStrike}
              yScale={yScale}
              centerX={innerWidth / 2}
              isDimmed={true}
              enableInteraction={false}
            />
          </g>
        )}

      {/* Main draggable pill - follows drag position, looks normal */}
      <StrikePricePill
        strikePrice={effectiveStrikePrice}
        yScale={yScale}
        innerWidth={innerWidth}
        isDraggingStrike={isDraggingStrike}
        spotPrice={spotPrice}
        asset={asset}
        onStrikePriceSelect={onStrikePriceSelect}
        onStrikeDragStart={onStrikeDragStart}
        ordersForStrike={ordersForStrike}
        onManageOrders={onManageOrders}
        isManageOrdersPanelOpen={isManageOrdersPanelOpen}
      />
    </g>
  );
}

interface PillRendererProps {
  strikePrice: number;
  yScale: YScale;
  centerX: number;
  isDimmed?: boolean;
  enableInteraction?: boolean;
  onStrikePriceSelect?: (price: number) => void;
  onStrikeDragStart?: () => void;
}

function PillRenderer({
  strikePrice,
  yScale,
  centerX,
  isDimmed = false,
  enableInteraction = false,
  onStrikePriceSelect,
  onStrikeDragStart,
}: PillRendererProps) {
  const priceText = formatStrikePrice(strikePrice);
  const pillWidth = chartConfig.strikePill.width;
  const pillHeight = chartConfig.strikePill.height;
  const centerY = yScale(strikePrice);

  // Track if this is a drag or click (only for interactive pills)
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const hasDragged = useRef(false);

  return (
    <g>
      {/* Opaque blocker behind dimmed pills to hide the line underneath */}
      {isDimmed && (
        <rect
          x={centerX - pillWidth / 2}
          y={centerY - pillHeight / 2}
          width={pillWidth}
          height={pillHeight}
          rx={8}
          ry={8}
          fill="rgba(0, 0, 0, 0.95)"
          style={{ pointerEvents: 'none' }}
        />
      )}

      <rect
        x={centerX - pillWidth / 2}
        y={centerY - pillHeight / 2}
        width={pillWidth}
        height={pillHeight}
        rx={chartConfig.strikePill.borderRadius}
        ry={chartConfig.strikePill.borderRadius}
        fill={isDimmed ? 'rgba(64, 64, 64, 0.4)' : 'rgba(64, 64, 64, 1)'}
        stroke={
          isDimmed ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.8)'
        }
        strokeWidth={2}
        style={{
          cursor: enableInteraction ? 'grab' : 'default',
          pointerEvents: enableInteraction ? 'all' : 'none',
        }}
        data-testid={
          enableInteraction ? `strike-price-pill-${strikePrice}` : undefined
        }
        onMouseDown={
          enableInteraction
            ? e => {
                // Only handle left mouse button
                if (e.button !== 0) {
                  return;
                }

                dragStartPos.current = { x: e.clientX, y: e.clientY };
                hasDragged.current = false;
                e.stopPropagation();
                e.preventDefault();

                // Add global mouse event listeners for better drag handling
                const handleMouseMove = (moveEvent: MouseEvent) => {
                  if (dragStartPos.current) {
                    const deltaX = Math.abs(
                      moveEvent.clientX - dragStartPos.current.x,
                    );
                    const deltaY = Math.abs(
                      moveEvent.clientY - dragStartPos.current.y,
                    );
                    const threshold = chartConfig.strikePill.dragThreshold;
                    if (deltaX > threshold || deltaY > threshold) {
                      if (!hasDragged.current) {
                        onStrikeDragStart?.();
                        hasDragged.current = true;
                      }
                    }
                  }
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }
            : undefined
        }
        onMouseUp={
          enableInteraction
            ? e => {
                const wasDragged = hasDragged.current;
                if (dragStartPos.current && !hasDragged.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  (window as any).lastDeselectionTime = Date.now();
                  onStrikePriceSelect?.(0);
                }
                dragStartPos.current = null;
                hasDragged.current = false;
                if (!wasDragged) {
                  e.stopPropagation();
                }
              }
            : undefined
        }
        onTouchStart={
          enableInteraction
            ? e => {
                const touch = e.touches[0];
                dragStartPos.current = { x: touch.clientX, y: touch.clientY };
                hasDragged.current = false;
                e.stopPropagation();
              }
            : undefined
        }
        onTouchMove={
          enableInteraction
            ? e => {
                if (dragStartPos.current && e.touches.length === 1) {
                  const touch = e.touches[0];
                  const deltaX = Math.abs(
                    touch.clientX - dragStartPos.current.x,
                  );
                  const deltaY = Math.abs(
                    touch.clientY - dragStartPos.current.y,
                  );
                  const threshold = chartConfig.strikePill.touchDragThreshold;
                  if (deltaX > threshold || deltaY > threshold) {
                    if (!hasDragged.current) {
                      onStrikeDragStart?.();
                      hasDragged.current = true;
                    }
                  }
                }
              }
            : undefined
        }
        onTouchEnd={
          enableInteraction
            ? e => {
                const wasDragged = hasDragged.current;
                if (dragStartPos.current && !hasDragged.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  (window as any).lastDeselectionTime = Date.now();
                  onStrikePriceSelect?.(0);
                }
                dragStartPos.current = null;
                hasDragged.current = false;
                if (!wasDragged) {
                  e.stopPropagation();
                }
              }
            : undefined
        }
      />

      {/* Drag indicator - thick vertical line on the left */}
      <line
        x1={centerX - pillWidth / 2 + 10}
        y1={centerY - 6}
        x2={centerX - pillWidth / 2 + 10}
        y2={centerY + 6}
        stroke={
          isDimmed ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.6)'
        }
        strokeWidth={4}
        strokeLinecap="round"
        style={{ pointerEvents: 'none' }}
      />

      {/* Price text in pill */}
      <text
        x={centerX}
        y={centerY + 1}
        fill={
          isDimmed ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.95)'
        }
        fontSize={chartConfig.strikePill.fontSize}
        textAnchor="middle"
        dominantBaseline="middle"
        fontWeight={chartConfig.strikePill.fontWeight}
        style={{
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {priceText}
      </text>

      {/* X button for deselection */}
      <text
        x={centerX + pillWidth / 2 - 15}
        y={centerY + 1}
        fill={
          isDimmed ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)'
        }
        fontSize="18"
        textAnchor="middle"
        dominantBaseline="middle"
        fontWeight="bold"
        style={{
          cursor: enableInteraction ? 'pointer' : 'default',
          pointerEvents: enableInteraction ? 'all' : 'none',
          userSelect: 'none',
        }}
        onMouseDown={
          enableInteraction
            ? e => {
                e.stopPropagation();
                e.preventDefault();
              }
            : undefined
        }
        onClick={
          enableInteraction
            ? e => {
                e.stopPropagation();
                e.preventDefault();
                onStrikePriceSelect?.(0);
              }
            : undefined
        }
        onTouchEnd={
          enableInteraction
            ? e => {
                e.stopPropagation();
                onStrikePriceSelect?.(0);
              }
            : undefined
        }
      >
        ×
      </text>
    </g>
  );
}

interface PriceIndicatorsProps {
  strikePrice: number;
  yScale: YScale;
  centerX: number;
  spotPrice: number;
  asset: Asset;
  isVisible: boolean;
  isDragPreview?: boolean;
}

interface AllStrikePricesProps {
  strikePrices: number[];
  selectedStrike?: number;
  yScale: YScale;
  centerX: number;
  spotPrice: number;
  asset: Asset;
  isVisible: boolean;
  isDraggingStrike?: boolean;
  innerHeight: number;
}

function SelectedStrikePriceIndicators({
  strikePrice,
  yScale,
  centerX,
  spotPrice,
  asset,
  isVisible,
}: PriceIndicatorsProps) {
  const pricingData = useOptionsPricing({
    spotPrice,
    strikePrice,
    asset,
  });

  if (!isVisible || strikePrice <= 0) {
    return null;
  }

  const centerY = yScale(strikePrice);
  const verticalOffset = chartConfig.priceIndicators.verticalOffset;

  // Bounds checking
  const callIndicatorY = centerY - verticalOffset;
  const putIndicatorY = centerY + verticalOffset;

  if (callIndicatorY < 0 || putIndicatorY < 0) {
    return null;
  }

  const formattedCallAsk = formatOptionPrice(pricingData.callAsk);
  const formattedPutAsk = formatOptionPrice(pricingData.putAsk);

  // Calculate background dimensions - wider to accommodate spread percentage and ABOVE/BELOW text
  const callPriceText = `$${formattedCallAsk}`;
  const putPriceText = `$${formattedPutAsk}`;
  const callSpreadText = `±${Math.round(pricingData.callSpreadPercent || 0)}%`;
  const putSpreadText = `±${Math.round(pricingData.putSpreadPercent || 0)}%`;

  // Calculate width to fit all content with proper spacing
  const maxTextLength = Math.max(
    callPriceText.length + callSpreadText.length + 6, // 6 chars for "ABOVE"
    putPriceText.length + putSpreadText.length + 6, // 6 chars for "BELOW"
  );
  const pillWidth = maxTextLength * 10;
  const pillHeight = chartConfig.priceIndicators.pillHeight;
  const pillPadding = chartConfig.priceIndicators.pillPadding;

  return (
    <g>
      {/* Call price background (dark green) - extends from strike pill, rounded top only */}
      <path
        d={`M ${centerX - pillWidth / 2} ${centerY}
            L ${centerX - pillWidth / 2} ${centerY - verticalOffset - pillHeight / 2 + 10}
            Q ${centerX - pillWidth / 2} ${centerY - verticalOffset - pillHeight / 2} ${centerX - pillWidth / 2 + 10} ${centerY - verticalOffset - pillHeight / 2}
            L ${centerX + pillWidth / 2 - 10} ${centerY - verticalOffset - pillHeight / 2}
            Q ${centerX + pillWidth / 2} ${centerY - verticalOffset - pillHeight / 2} ${centerX + pillWidth / 2} ${centerY - verticalOffset - pillHeight / 2 + 10}
            L ${centerX + pillWidth / 2} ${centerY}
            Z`}
        fill="rgba(22, 101, 52, 0.9)"
        style={{ pointerEvents: 'none' }}
      />

      {/* ABOVE text - left aligned like BELOW */}
      <text
        x={centerX - pillWidth / 2 + pillPadding}
        y={centerY - verticalOffset}
        textAnchor="start"
        dominantBaseline="central"
        fill={brandRgba('green', 0.9)}
        fontSize={chartConfig.priceIndicators.fontSize.label}
        fontWeight={chartConfig.priceIndicators.fontWeight.label}
        style={{ pointerEvents: 'none' }}
      >
        ABOVE
      </text>

      {/* Call price indicator - center */}
      <text
        x={centerX + pillPadding}
        y={centerY - verticalOffset}
        textAnchor="middle"
        dominantBaseline="central"
        fill="rgba(255, 255, 255, 0.95)"
        fontSize={chartConfig.priceIndicators.fontSize.price}
        fontWeight={chartConfig.priceIndicators.fontWeight.price}
        style={{ pointerEvents: 'none' }}
      >
        ${formattedCallAsk}
      </text>

      {/* Call spread percentage - right aligned */}
      <text
        x={centerX + pillWidth / 2 - pillPadding}
        y={centerY - verticalOffset}
        textAnchor="end"
        dominantBaseline="central"
        fill="rgba(255, 255, 255, 0.6)"
        fontSize={chartConfig.priceIndicators.fontSize.spread}
        fontWeight={chartConfig.priceIndicators.fontWeight.spread}
        style={{ pointerEvents: 'none' }}
      >
        {callSpreadText}
      </text>

      {/* Put price background (dark red) - extends from strike pill, rounded bottom only */}
      <path
        d={`M ${centerX - pillWidth / 2} ${centerY}
            L ${centerX - pillWidth / 2} ${centerY + verticalOffset + pillHeight / 2 - 10}
            Q ${centerX - pillWidth / 2} ${centerY + verticalOffset + pillHeight / 2} ${centerX - pillWidth / 2 + 10} ${centerY + verticalOffset + pillHeight / 2}
            L ${centerX + pillWidth / 2 - 10} ${centerY + verticalOffset + pillHeight / 2}
            Q ${centerX + pillWidth / 2} ${centerY + verticalOffset + pillHeight / 2} ${centerX + pillWidth / 2} ${centerY + verticalOffset + pillHeight / 2 - 10}
            L ${centerX + pillWidth / 2} ${centerY}
            Z`}
        fill="rgba(127, 29, 29, 0.9)"
        style={{ pointerEvents: 'none' }}
      />

      {/* BELOW text - centered and in bottom part of section */}
      <text
        x={centerX - pillWidth / 2 + pillPadding}
        y={centerY + verticalOffset}
        textAnchor="start"
        dominantBaseline="central"
        fill={brandRgba('red', 0.9)}
        fontSize={chartConfig.priceIndicators.fontSize.label}
        fontWeight={chartConfig.priceIndicators.fontWeight.label}
        style={{ pointerEvents: 'none' }}
      >
        BELOW
      </text>

      {/* Put price indicator - left of center */}
      <text
        x={centerX + pillPadding}
        y={centerY + verticalOffset}
        textAnchor="middle"
        dominantBaseline="central"
        fill="rgba(255, 255, 255, 0.95)"
        fontSize={chartConfig.priceIndicators.fontSize.price}
        fontWeight={chartConfig.priceIndicators.fontWeight.price}
        style={{ pointerEvents: 'none' }}
      >
        ${formattedPutAsk}
      </text>

      {/* Put spread percentage - right of center with spacing */}
      <text
        x={centerX + pillWidth / 2 - pillPadding}
        y={centerY + verticalOffset}
        textAnchor="end"
        dominantBaseline="central"
        fill="rgba(255, 255, 255, 0.6)"
        fontSize={chartConfig.priceIndicators.fontSize.spread}
        fontWeight={chartConfig.priceIndicators.fontWeight.spread}
        style={{ pointerEvents: 'none' }}
      >
        {putSpreadText}
      </text>
    </g>
  );
}

function AllStrikePrices({
  strikePrices,
  selectedStrike,
  yScale,
  spotPrice,
  asset,
  isVisible,
  isDraggingStrike = false,
  innerHeight,
}: AllStrikePricesProps) {
  // Get bulk pricing for all strikes at once
  const bulkPricing = useBulkOptionsPricing({
    spotPrice,
    strikePrices,
    asset,
  });

  // Only show when a strike is selected
  if (!isVisible || strikePrices.length === 0 || !selectedStrike) {
    return null;
  }

  // Use unified visible strikes logic - this will match what's shown in price labels
  const strikesToShow = getVisibleStrikes(
    strikePrices,
    yScale,
    innerHeight,
    selectedStrike,
  );

  return (
    <g>
      {strikePrices.map(strike => {
        const centerY = yScale(strike);
        const verticalOffset = 10;
        const rightEdge = 45; // Right edge of existing price label backgrounds

        // During drag, show option prices for all strikes including selected one
        // When not dragging, skip the selected strike (it has centered indicators)
        if (
          !isDraggingStrike &&
          selectedStrike &&
          Math.abs(strike - selectedStrike) < 0.01
        ) {
          return null;
        }

        // Skip this strike if it doesn't pass the density check
        if (!strikesToShow.has(strike)) {
          return null;
        }

        // Get pricing for this strike from the bulk pricing map
        const pricing = bulkPricing.get(strike);
        if (!pricing) {
          return null;
        }

        // Bounds checking
        const callIndicatorY = centerY - verticalOffset;
        const putIndicatorY = centerY + verticalOffset;

        if (callIndicatorY < 0 || putIndicatorY < 0) {
          return null;
        }

        const formattedCallAsk = formatOptionPrice(pricing.callAsk);
        const formattedPutAsk = formatOptionPrice(pricing.putAsk);

        return (
          <g key={strike}>
            {/* Call price (green, above) - showing ask price */}
            <text
              x={rightEdge}
              y={centerY - verticalOffset}
              textAnchor="end"
              dominantBaseline="middle"
              fill="rgba(34, 197, 94, 0.7)"
              fontSize={chartConfig.rightPriceLabels.fontSize.price}
              fontWeight={chartConfig.rightPriceLabels.fontWeight.price}
              style={{ pointerEvents: 'none' }}
            >
              ${formattedCallAsk}
            </text>
            {/* Call spread percentage */}
            <text
              x={rightEdge + 5}
              y={centerY - verticalOffset}
              textAnchor="start"
              dominantBaseline="middle"
              fill="rgba(255, 255, 255, 0.3)"
              fontSize={chartConfig.rightPriceLabels.fontSize.spread}
              style={{ pointerEvents: 'none' }}
            >
              ±{Math.round(pricing.callSpreadPercent)}%
            </text>

            {/* Put price (red, below) - showing ask price */}
            <text
              x={rightEdge}
              y={centerY + verticalOffset}
              textAnchor="end"
              dominantBaseline="middle"
              fill="rgba(239, 68, 68, 0.7)"
              fontSize={chartConfig.rightPriceLabels.fontSize.price}
              fontWeight={chartConfig.rightPriceLabels.fontWeight.price}
              style={{ pointerEvents: 'none' }}
            >
              ${formattedPutAsk}
            </text>
            {/* Put spread percentage */}
            <text
              x={rightEdge + 5}
              y={centerY + verticalOffset}
              textAnchor="start"
              dominantBaseline="middle"
              fill="rgba(255, 255, 255, 0.3)"
              fontSize={chartConfig.rightPriceLabels.fontSize.spread}
              style={{ pointerEvents: 'none' }}
            >
              ±{Math.round(pricing.putSpreadPercent)}%
            </text>
          </g>
        );
      })}
    </g>
  );
}

interface StrikePricePillProps {
  strikePrice: number;
  yScale: YScale;
  innerWidth: number;
  isDraggingStrike: boolean;
  spotPrice: number;
  asset: Asset;
  onStrikePriceSelect: (price: number) => void;
  onStrikeDragStart: () => void;
  ordersForStrike: Order[];
  onManageOrders?: (orders: Order[], strikePrice: number) => void;
  isManageOrdersPanelOpen?: boolean;
}

function StrikePricePill({
  strikePrice,
  yScale,
  innerWidth,
  isDraggingStrike,
  spotPrice,
  asset,
  onStrikePriceSelect,
  onStrikeDragStart,
  ordersForStrike,
  onManageOrders,
  isManageOrdersPanelOpen = false,
}: StrikePricePillProps) {
  const centerX = innerWidth / 2;
  const centerY = yScale(strikePrice);

  return (
    <g>
      {/* Price indicators for selected strike - hide during drag */}
      {!isDraggingStrike && strikePrice > 0 && (
        <SelectedStrikePriceIndicators
          strikePrice={strikePrice}
          yScale={yScale}
          centerX={centerX}
          spotPrice={spotPrice}
          asset={asset}
          isVisible={true}
        />
      )}

      {/* Main pill - always looks normal, not dimmed */}
      <PillRenderer
        strikePrice={strikePrice}
        yScale={yScale}
        centerX={centerX}
        isDimmed={false}
        enableInteraction={true}
        onStrikePriceSelect={onStrikePriceSelect}
        onStrikeDragStart={onStrikeDragStart}
      />

      {/* Manage orders button - appears to the left of the pill when there are pending orders */}
      {!isDraggingStrike && ordersForStrike.length > 0 && onManageOrders && (
        <foreignObject
          x={centerX / 2 - 175 / 4 - 48 / 2}
          y={centerY - 18}
          width={48}
          height={36}
          style={{ pointerEvents: 'none' }}
        >
          <button
            id="chart-manage-orders-button"
            onClick={e => {
              e.stopPropagation();
              onManageOrders(ordersForStrike, strikePrice);
            }}
            className={`w-12 h-10 border-[3px] rounded-xl flex items-center justify-center transition-colors relative ${
              isManageOrdersPanelOpen
                ? 'bg-blue-300 border-blue-300 hover:bg-blue-200 active:bg-blue-100'
                : 'bg-blue-500/10 hover:bg-blue-500/20 active:bg-blue-500/30 border-blue-400/60'
            }`}
            style={{
              backdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
              touchAction: 'auto',
            }}
            aria-label="Manage orders"
          >
            <FontAwesomeIcon
              icon={faFileLines}
              className={
                isManageOrdersPanelOpen ? 'text-blue-900' : 'text-blue-300'
              }
              style={{ pointerEvents: 'none', fontSize: '16px' }}
            />
            {/* Order count badge */}
            <div
              className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                isManageOrdersPanelOpen
                  ? 'bg-blue-900 text-blue-100'
                  : 'bg-blue-500 text-white'
              }`}
              style={{ pointerEvents: 'none' }}
            >
              {ordersForStrike.length}
            </div>
          </button>
        </foreignObject>
      )}
    </g>
  );
}

interface PriceLabelsProps {
  strikePrices: number[];
  yScale: YScale;
  innerHeight: number;
  strikePrice?: number;
  isDraggingStrike: boolean;
  previewStrikePrice: number | null;
  onStrikePriceSelect?: (price: number) => void;
}

export function PriceLabels({
  strikePrices,
  yScale,
  innerHeight,
  strikePrice,
  isDraggingStrike,
  previewStrikePrice,
  onStrikePriceSelect,
}: PriceLabelsProps) {
  // Get unified visible strikes logic
  const strikesToShow = getVisibleStrikes(
    strikePrices,
    yScale,
    innerHeight,
    strikePrice,
  );

  return (
    <>
      {strikePrices
        .filter(price => {
          const y = yScale(price);
          return y >= 0 && y <= innerHeight;
        })
        .filter(price => {
          // Only show strikes that pass the density check
          return strikesToShow.has(price);
        })
        .filter(price => {
          // During drag, show all strike labels including selected one
          // When not dragging, hide the selected strike's label
          return isDraggingStrike || price !== strikePrice;
        })
        .map(price => {
          const y = yScale(price);
          const priceText = formatStrikePrice(price);
          const isSelected = price === strikePrice;
          const isPreview = isDraggingStrike && price === previewStrikePrice;
          if (isPreview) {
            return null;
          }
          return (
            <g key={`label-${price}`}>
              <rect
                x={4}
                y={y - 8}
                width={52}
                height={16}
                fill={
                  isSelected && !isDraggingStrike
                    ? brandRgba('green', 0.2)
                    : 'rgba(0, 0, 0, 0.7)'
                }
                stroke={
                  isSelected && !isDraggingStrike
                    ? brandRgba('green', 0.5)
                    : 'none'
                }
                strokeWidth={1}
                rx={2}
                style={{ cursor: 'pointer' }}
                onClick={() => onStrikePriceSelect?.(price)}
              />
              <text
                x={56}
                y={y}
                fill={
                  isSelected && !isDraggingStrike
                    ? brandRgba('green', 1)
                    : 'rgba(255, 255, 255, 0.9)'
                }
                fontSize="12"
                textAnchor="end"
                dominantBaseline="middle"
                style={{ cursor: 'pointer' }}
                onClick={() => onStrikePriceSelect?.(price)}
              >
                {priceText}
              </text>
            </g>
          );
        })}
    </>
  );
}
